import React, { createContext, useContext, useEffect, useState } from 'react';
import defaultPersona from './default';

const PersonaContext = createContext(defaultPersona);
const SLUG_RE = /^\/r\/([a-z0-9]+)\/?$/i;
const STORAGE_KEY = 'portfolio.persona';

const readStoredPersona = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeStoredPersona = (p) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(p));
  } catch {
    // sessionStorage unavailable (private mode, quota) — ignore.
  }
};

export const PersonaProvider = ({ children }) => {
  // Restore from sessionStorage on first render so a refresh keeps whichever
  // persona was last loaded via /r/<slug>. Falls back to defaultPersona.
  const [persona, setPersona] = useState(() => readStoredPersona() || defaultPersona);

  useEffect(() => {
    const match = window.location.pathname.match(SLUG_RE);
    const slug = match ? match[1] : null;
    if (!slug) return;

    let cancelled = false;
    fetch(`/personas/${slug}.json`, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => {
        if (cancelled || !p) return;
        setPersona(p);
        writeStoredPersona(p);
        // Strip slug from address bar so the recruiter copies a clean root URL.
        window.history.replaceState({}, '', '/');
      })
      .catch(() => {
        // Network or parse error: stay on whatever persona was already loaded
        // (either sessionStorage-restored or default). Strip URL anyway so the
        // unknown-slug attempt is not visible.
        if (!cancelled) window.history.replaceState({}, '', '/');
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return <PersonaContext.Provider value={persona}>{children}</PersonaContext.Provider>;
};

export const usePersona = () => useContext(PersonaContext);
